
import React, { useEffect, useReducer, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Switch } from "antd";
import PolicyEditor from "../../hoc/textEditor/policyEditor";
import {
    addPrivacyPolicy,
    editPrivacyPolicy,
    getPolicyById,
} from "../../config/services/employeeServices";
import { ErrorMessage, reducer, type, Toastify } from "../helper/Constant";
import { Loader } from "../svg";
import ResponseFilter from "../../ResponseFilter";
import ViewUploadedPdf from "./PrivacyModal/ViewUploadedPdf";

const initialState = {
    title: "",
    titleErrorMsg: "",
    department: "",
    departmentErrorMsg: "",
    status: "",
    statusErrorMsg: "",
    descriptionErrorMsg: "",
    resMsg: false,
    successMessage: "",
    resFailMsg: false,
    responseMessage: "",
    loader: false,
    editorState: EditorState.createEmpty(),
    isNewVersion: false,
    initialData: null,
    selectedFile: null,
    existingFileName: "",
    fileErrorMsg: "",
    openViewDoc: false,
    docToView: "",
    existingDocLink: "",
    docFileType: "",
};

const AddEditPolicy = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const history = useHistory();
    const { id } = useParams();
    const toAddOrEdit = id ? "Edit" : "Add";

    const {
        title,
        titleErrorMsg,
        department,
        departmentErrorMsg,
        status,
        statusErrorMsg,
        descriptionErrorMsg,
        resMsg,
        successMessage,
        resFailMsg,
        responseMessage,
        loader,
        editorState,
        isNewVersion,
        initialData,
        selectedFile,
        existingFileName,
        fileErrorMsg,
        openViewDoc,
        docToView,
        existingDocLink,
        docFileType,
    } = state;

    useEffect(() => {
        if (id) {
            dispatch({ type: type, payload: { loader: true } });
            getPolicyById({ policyId: id })
                .then((res) => {
                    const { status, resData } = ResponseFilter(res);
                    if (status && resData && resData.list && resData.list.length > 0) {
                        const policy = resData.list[0]; // Access the first item in the list
                        dispatch({
                            type: type,
                            payload: {
                                title: policy.title,
                                department: policy.department || "",
                                status: policy.status,
                                loader: false,
                                editorState: createEditorStateFromHTML(policy.description),
                                existingFileName: policy.fileName || "",
                                existingDocLink: policy.docLink || "",
                                initialData: {
                                    title: policy.title,
                                    department: policy.department || "",
                                    status: policy.status,
                                    description: policy.description || "",
                                    fileName: policy.fileName || "",
                                },
                            },
                        });
                    } else {
                        dispatch({
                            type: type,
                            payload: { loader: false, resFailMsg: true, responseMessage: "Failed to fetch details" },
                        });
                    }
                })
                .catch((err) => {
                    dispatch({
                        type: type,
                        payload: { loader: false, resFailMsg: true, responseMessage: err.message },
                    });
                });
        }
    }, [id]);

    const createEditorStateFromHTML = (html) => {
        if (html) {
            const blocksFromHtml = htmlToDraft(html);
            const { contentBlocks, entityMap } = blocksFromHtml;
            const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
            return EditorState.createWithContent(contentState);
        }
        return EditorState.createEmpty();
    };

    const onEditorStateChange = (editorState) => {
        dispatch({
            type: type,
            payload: {
                editorState,
                descriptionErrorMsg: "",
                fileErrorMsg: "",
            },
        });
    };

    const handleTextChange = (e) => {
        const { name, value } = e.target;
        dispatch({
            type: type,
            payload: {
                [name]: value,
                [`${name}ErrorMsg`]: "",
            },
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
            if (!allowedTypes.includes(file.type)) {
                dispatch({
                    type: type,
                    payload: { fileErrorMsg: "Only PDF and Images (JPG, PNG) are allowed", selectedFile: null },
                });
                return;
            }
            dispatch({
                type: type,
                payload: {
                    selectedFile: file,
                    fileErrorMsg: "",
                    descriptionErrorMsg: "",
                    existingFileName: ""
                },
            });
        }
    };

    const handleRemoveFile = () => {
        dispatch({
            type: type,
            payload: {
                selectedFile: null,
                existingFileName: "",
                existingDocLink: "",
                fileErrorMsg: "",
            }
        });
        // Reset file input if necessary
        const fileInput = document.getElementById("policyFile");
        if (fileInput) fileInput.value = "";
    };

    const handleViewDocument = () => {
        if (selectedFile) {
            // Preview newly selected file
            const fileURL = URL.createObjectURL(selectedFile);
            dispatch({
                type: type,
                payload: {
                    openViewDoc: true,
                    docToView: fileURL,
                    docFileType: selectedFile.type,
                },
            });
        } else if (existingDocLink) {
            // Preview existing file
            dispatch({
                type: type,
                payload: {
                    openViewDoc: true,
                    docToView: existingDocLink,
                    docFileType: "",
                },
            });
        }
    };

    const closeViewModal = () => {
        dispatch({
            type: type,
            payload: {
                openViewDoc: false,
                docToView: "",
                docFileType: "",
            },
        });
    };

    const handleValidation = () => {
        let isValid = true;
        const errors = {};

        if (!title.trim()) {
            errors.titleErrorMsg = "Title is required";
            isValid = false;
        }
        if (!department.trim()) {
            errors.departmentErrorMsg = "Department is required";
            isValid = false;
        }
        if (!status) {
            errors.statusErrorMsg = "Status is required";
            isValid = false;
        }

        const plainText = editorState.getCurrentContent().getPlainText().trim();
        if (!plainText && !selectedFile && !existingFileName) {
            errors.descriptionErrorMsg = "Either description or document is required";
            errors.fileErrorMsg = "Either description or document is required";
            isValid = false;
        }

        if (!isValid) {
            dispatch({ type: type, payload: errors });
        }
        return isValid;
    };

    const isChanged = () => {
        const descriptionHtml = draftToHtml(convertToRaw(editorState.getCurrentContent())).trim();

        const currentData = {
            title: (title || "").trim(),
            department: (department || "").trim(),
            status: String(status || ""),
            description: descriptionHtml
        };

        const comparisonData = initialData ? {
            title: (initialData.title || "").trim(),
            department: (initialData.department || "").trim(),
            status: String(initialData.status || ""),
            description: (initialData.description || "").trim()
        } : {
            title: "",
            department: "",
            status: "",
            description: ""
        };

        return (
            currentData.title !== comparisonData.title ||
            currentData.department !== comparisonData.department ||
            currentData.status !== comparisonData.status ||
            currentData.description !== comparisonData.description ||
            selectedFile !== null ||
            existingFileName !== (initialData ? initialData.fileName : "")
        );
    };

    const handleSwitchChange = (checked) => {
        dispatch({ type: type, payload: { isNewVersion: checked } });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (handleValidation()) {
            dispatch({ type: type, payload: { loader: true } });

            const descriptionHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
            const params = new FormData();
            params.append("title", title);
            params.append("department", department);
            params.append("description", descriptionHtml);
            params.append("status", status);
            params.append("isNewVersion", isNewVersion);

            if (selectedFile) {
                params.append("doc", selectedFile);
            } else if (toAddOrEdit === "Edit" && !existingFileName) {
                // If it was removed during edit
                params.append("docLink", "");
                params.append("fileName", "");
            }

            const apiCall = toAddOrEdit === "Add" ? addPrivacyPolicy(params) : editPrivacyPolicy(params);

            // For Edit, we might need to append privacyId if using FormData or separate param
            if (toAddOrEdit === "Edit") {
                params.append("privacyId", id);
            }

            apiCall
                .then((res) => {
                    dispatch({ type: type, payload: { loader: false } });
                    const { status: apiStatus, resData } = ResponseFilter(res);
                    if (apiStatus) {
                        dispatch({
                            type: type,
                            payload: {
                                resMsg: true,
                                successMessage: `Policy ${toAddOrEdit}ed Successfully`,
                            },
                        });
                        setTimeout(() => {
                            history.push("/policies");
                        }, 1500);
                    } else {
                        dispatch({
                            type: type,
                            payload: {
                                resFailMsg: true,
                                responseMessage: resData.error?.responseMessage || "Something went wrong",
                            },
                        });
                    }
                })
                .catch((err) => {
                    dispatch({
                        type: type,
                        payload: { loader: false, resFailMsg: true, responseMessage: err.message },
                    });
                });
        }
    };

    return (
        <div className="body_container cm_bold marginTop">
            {loader && <Loader />}
            <Toastify
                resMsg={resMsg}
                resMsgFail={resFailMsg}
                text={successMessage}
                responseMessage={responseMessage}
            />

            <div className="cm_breadcrumb cm_bold mb-4">
                <ul>
                    <li
                        style={{ cursor: "pointer", color: "black", textTransform: "uppercase" }}
                        onClick={() => history.push("/policies")}
                    >
                        Policies
                        <div className="cm_breadcrumb_arrow"></div>
                    </li>
                    <li>{toAddOrEdit} Policy</li>
                </ul>
            </div>

            <div className="card-body bg-white rounded shadow-sm p-4">
                <h4 className="mb-4">{toAddOrEdit} Policy</h4>

                <div className="row">
                    <div className="col-md-6 form-group">
                        <label className="sub">Title</label>
                        <input
                            className={`form-control cm_box ${titleErrorMsg ? "input_error" : ""}`}
                            type="text"
                            name="title"
                            value={title}
                            onChange={handleTextChange}
                            placeholder="Enter Title"
                        />
                        <ErrorMessage errMsg={titleErrorMsg} />
                    </div>

                    <div className="col-md-6 form-group">
                        <label className="sub">Department</label>
                        <input
                            className={`form-control cm_box ${departmentErrorMsg ? "input_error" : ""}`}
                            type="text"
                            name="department"
                            value={department}
                            onChange={handleTextChange}
                            placeholder="Enter Department"
                        />
                        <ErrorMessage errMsg={departmentErrorMsg} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6 form-group">
                        <label className="sub">Status</label>
                        <select
                            className={`form-control cm_box ${statusErrorMsg ? "input_error" : ""}`}
                            name="status"
                            value={status}
                            onChange={handleTextChange}
                        >
                            <option value="">Select Status</option>
                            <option value="1">Active</option>
                            <option value="2">Draft</option>
                            <option value="3">Archived</option>
                        </select>
                        <ErrorMessage errMsg={statusErrorMsg} />
                    </div>
                    {toAddOrEdit === "Edit" && (
                        <div className="col-md-6 form-group d-flex align-items-center">
                            <label className="sub mr-3 mb-0">Update as New Version</label>
                            <Switch checked={isNewVersion} onChange={handleSwitchChange} />
                        </div>
                    )}
                </div>

                <div className="row">
                    <div className="col-md-12 form-group">
                        <label className="sub">Description</label>
                        <div className={`${descriptionErrorMsg ? "border border-danger rounded" : ""}`} style={{ fontWeight: "normal" }}>
                            <PolicyEditor
                                editorState={editorState}
                                onEditorStateChange={onEditorStateChange}
                                placeholder="Enter policy description..."
                                toolbarHidden={false}
                                toolbar={{
                                    options: ['inline', 'colorPicker', 'list', 'link', 'embedded', 'image'],
                                    inline: {
                                        inDropdown: false,
                                        options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                                    },
                                    list: { inDropdown: false, options: ['unordered'] },
                                    textAlign: { inDropdown: false },
                                    link: { inDropdown: false },
                                    history: { inDropdown: false },
                                }}
                            />
                        </div>
                        <ErrorMessage errMsg={descriptionErrorMsg} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12 form-group">
                        <label className="sub">Upload Document (PDF only)</label>
                        <div className="d-flex align-items-center">
                            <div className="custom-file flex-grow-1">
                                <input
                                    type="file"
                                    className="custom-file-input"
                                    id="policyFile"
                                    onChange={handleFileChange}
                                    accept=".pdf*"
                                />
                                <label className="custom-file-label" htmlFor="policyFile">
                                    {selectedFile ? selectedFile.name : (existingFileName || "Choose file...")}
                                </label>
                            </div>
                            {(selectedFile || existingFileName) && (
                                <>
                                    <button
                                        className="btn btn-outline-primary ml-2"
                                        type="button"
                                        onClick={handleViewDocument}
                                        style={{ height: 'calc(1.5em + .75rem + 2px)', whiteSpace: 'nowrap' }}
                                    >
                                        <i className="fa fa-eye mr-1"></i> View
                                    </button>
                                    <button
                                        className="btn btn-outline-danger ml-2"
                                        type="button"
                                        onClick={handleRemoveFile}
                                        style={{ height: 'calc(1.5em + .75rem + 2px)', whiteSpace: 'nowrap' }}
                                    >
                                        <i className="fa fa-times mr-1"></i> Remove
                                    </button>
                                </>
                            )}
                        </div>
                        {existingFileName && !selectedFile && (
                            <div className="small text-muted mt-1">
                                <i className="fa fa-info-circle mr-1"></i>
                                Using current file: {existingFileName}
                            </div>
                        )}
                        <ErrorMessage errMsg={fileErrorMsg} />
                    </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                    <button
                        className="btn btn-secondary mr-2"
                        onClick={() => history.push("/policies")}
                    >
                        Cancel
                    </button>
                    <button
                        className={`btn ${isChanged() ? "btn-primary" : "btn-secondary"}`}
                        onClick={handleSubmit}
                        disabled={!isChanged()}
                    >
                        Save
                    </button>
                </div>
            </div>

            <ViewUploadedPdf
                show={openViewDoc}
                doc={docToView}
                fileType={docFileType}
                closeModal={closeViewModal}
            />
        </div>
    );
};

export default AddEditPolicy;
